import https from 'https';
import {NextApiRequest, NextApiResponse} from 'next';
import {IncomingHttpHeaders} from 'undici-types/header';
import {withRequestIdContext} from 'core/modules/request-id';

interface IEnvelopeHeader {
    dsn: string;
    [key: string]: unknown;
}

interface IEventPayload {
    user?: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ip_address?: string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

interface IFetchOptions {
    method: string;
    body: string;
    headers: Record<string, string>;
    agent?: https.Agent;
}

async function getRawBody(req: NextApiRequest): Promise<string> {
    return new Promise((resolve, reject) => {
        const chunks: Buffer[] = [];

        req.on('data', (chunk: Buffer) => chunks.push(chunk));
        req.on('end', () => {
            const buffer = Buffer.concat(chunks);
            resolve(buffer.toString('utf8'));
        });
        req.on('error', (error: Error) => reject(error));
    });
}

function isValidHttpMethod(method: string | undefined): method is 'POST' {
    return 'POST' === method;
}

export function parseClientIp(req: NextApiRequest): string {
    const xForwardedFor = req.headers['x-forwarded-for'];
    const xRealIp = req.headers['x-real-ip'];
    const socketAddress = req.socket?.remoteAddress;

    if ('string' === typeof xForwardedFor) {
        return xForwardedFor.split(',')[0]?.trim() || '';
    }

    if (Array.isArray(xForwardedFor) && 0 < xForwardedFor.length) {
        return xForwardedFor[0].split(',')[0]?.trim() || '';
    }

    if ('string' === typeof xRealIp) {
        return xRealIp;
    }

    if (Array.isArray(xRealIp) && 0 < xRealIp.length) {
        return xRealIp[0];
    }

    return socketAddress || '';
}

// Внутренний апи роут для туннелирования запросов к сентри, пока что не используется
async function forwardToSentry(
    targetUrl: string,
    body: string,
    headers: IncomingHttpHeaders,
    isDevelopment: boolean,
): Promise<{status: number; text: string}> {
    const fetchOptions: IFetchOptions = {
        method: 'POST',
        body,
        headers: {
            ['Content-Type']: 'application/x-sentry-envelope',
            ['User-Agent']: (headers['user-agent'] as string) || '',
        },
    };

    if (isDevelopment) {
        fetchOptions.agent = new https.Agent({rejectUnauthorized: false});
    }

    try {
        const response = await fetch(targetUrl, fetchOptions);
        const responseText = await response.text();

        return {
            status: response.status,
            text: responseText,
        };
    } catch (error) {
        const fetchError = error as Error;
        throw new Error(`Failed to forward to Sentry: ${fetchError.message}`);
    }
}

export default async function handler(req: NextApiRequest, res: NextApiResponse): Promise<void> {
    // eslint-disable-next-line complexity
    return withRequestIdContext(req, res, async () => {
        if (!isValidHttpMethod(req.method)) {
            res.setHeader('Allow', ['POST']);
            res.status(405).end(`Method ${req.method} Not Allowed`);
            return;
        }

        try {
            const rawBody = await getRawBody(req);

            if (!rawBody || 0 === rawBody.trim().length) {
                res.status(400).json({error: 'Empty request body'});
                return;
            }

            const lines = rawBody.trim().split('\n');
            if (2 > lines.length) {
                res.status(400).json({error: 'Invalid envelope format'});
                return;
            }

            let envelopeHeader: IEnvelopeHeader;
            try {
                envelopeHeader = JSON.parse(lines[0]) as IEnvelopeHeader;
            } catch (error) {
                res.status(400).json({error: 'Invalid envelope header JSON'});
                return;
            }

            if (!envelopeHeader.dsn) {
                res.status(400).json({error: 'Missing DSN in envelope header'});
                return;
            }

            let dsnUrl: URL;
            try {
                dsnUrl = new URL(envelopeHeader.dsn);
            } catch (error) {
                res.status(400).json({error: 'Invalid DSN format'});
                return;
            }

            const sentryHost = dsnUrl.host;
            const pathSegments = dsnUrl.pathname.split('/').filter(Boolean);
            const projectId = pathSegments[0];

            if (!projectId) {
                res.status(400).json({error: 'Invalid project ID in DSN'});
                return;
            }

            const clientIp = parseClientIp(req);

            if (clientIp && 3 <= lines.length) {
                try {
                    const eventPayload = JSON.parse(lines[2]) as IEventPayload;

                    if (eventPayload && 'object' === typeof eventPayload) {
                        // eslint-disable-next-line max-depth
                        if (!eventPayload.user) {
                            eventPayload.user = {};
                        }
                        eventPayload.user.ip_address = clientIp;

                        lines[2] = JSON.stringify(eventPayload);
                    }
                } catch (error) {
                    const parseError = error as Error;
                    console.warn('[Sentry Proxy] Could not parse event payload for IP update:', parseError.message);
                }
            }

            const updatedBody = lines.join('\n');
            const targetUrl = `https://${sentryHost}/api/${projectId}/envelope/`;

            console.info('[Sentry Proxy] Proxying to:', targetUrl);
            console.info('[Sentry Proxy] Client IP:', clientIp || 'not detected');

            const isDevelopment = 'development' === process.env.NODE_ENV;
            const response = await forwardToSentry(targetUrl, updatedBody, req.headers, isDevelopment);

            if (200 !== response.status) {
                console.error(
                    '[Sentry Proxy] Sentry responded with:',
                    response.status,
                    response.text.substring(0, 500),
                );
            } else {
                console.info('[Sentry Proxy] Successfully forwarded to Sentry');
            }

            res.status(response.status).send(response.text);
        } catch (error) {
            const caughtError = error as Error;
            console.error('[Sentry Proxy] Unexpected error:', caughtError);

            res.status(500).json({
                error: 'Failed to proxy to Sentry',
                details: caughtError.message,
                timestamp: new Date().toISOString(),
            });
        }
    });
}

export const config = {
    api: {
        bodyParser: false,
        externalResolver: true,
    },
};
